import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import SearchableDropdown from '../TicketComponents/SearchableDropdown';
import { getApiErrorMessage } from '../../service/errorHandler';

const emptyForm = {
  annualLeave: '',
  medicalLeave: '',
  hospitalLeave: '',
  manager: '',
};

const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const UpdateUserInformationPage = () => {
  const apiClient = useApiClient();
  const [users, setUsers] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/users/detail-list');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load users.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserInformation = async (email) => {
    setSelectedEmail(email);
    setMessage('');
    setError('');

    if (!email) {
      setForm(emptyForm);
      return;
    }

    try {
      const response = await apiClient.get(`/users/leave-available/${encodeURIComponent(email)}`);
      setForm({
        annualLeave: toInputValue(response.data?.annual),
        medicalLeave: toInputValue(response.data?.medical),
        hospitalLeave: toInputValue(response.data?.hospital),
        manager: '',
      });
    } catch (err) {
      setForm(emptyForm);
      setError(getApiErrorMessage(err, 'Unable to load the selected user information.'));
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const parseLeave = (value, label) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      throw new Error(`${label} must be zero or a positive number.`);
    }
    return parsed;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!selectedEmail) {
      setError('Select a user.');
      return;
    }

    let payload;
    try {
      payload = {
        userEmail: selectedEmail,
        annualLeave: parseLeave(form.annualLeave, 'Annual leave'),
        medicalLeave: parseLeave(form.medicalLeave, 'Medical leave'),
        hospitalLeave: parseLeave(form.hospitalLeave, 'Hospital leave'),
        manager: form.manager.trim() || null,
      };
    } catch (validationError) {
      setError(validationError.message);
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/users/update-user-details', payload);
      setMessage('User information updated successfully.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to update user information.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <div>
          <h1 className="mb-1">Update Information</h1>
          <p className="text-muted mb-0">Update leave balances and the user&apos;s manager.</p>
        </div>
        <Link className="btn btn-outline-secondary" to="/manage-user">Back</Link>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-muted">Loading users...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="informationUser">User</label>
                <SearchableDropdown
                  id="informationUser"
                  options={users}
                  selectedValue={selectedEmail}
                  onSelectedChange={loadUserInformation}
                  placeholder="Select user"
                />
              </div>

              <fieldset disabled={!selectedEmail}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="annualLeave">Annual Leave</label>
                    <input
                      id="annualLeave"
                      name="annualLeave"
                      type="number"
                      min="0"
                      step="0.5"
                      className="form-control"
                      value={form.annualLeave}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="medicalLeave">Medical Leave</label>
                    <input
                      id="medicalLeave"
                      name="medicalLeave"
                      type="number"
                      min="0"
                      step="0.5"
                      className="form-control"
                      value={form.medicalLeave}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label" htmlFor="hospitalLeave">Hospital Leave</label>
                    <input
                      id="hospitalLeave"
                      name="hospitalLeave"
                      type="number"
                      min="0"
                      step="0.5"
                      className="form-control"
                      value={form.hospitalLeave}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label" htmlFor="manager">Manager</label>
                    <input
                      id="manager"
                      name="manager"
                      className="form-control"
                      value={form.manager}
                      onChange={handleChange}
                      placeholder="Manager name or email"
                    />
                    <div className="form-text">
                      The current detail-list response does not return manager, so enter or replace it here.
                    </div>
                  </div>
                </div>
              </fieldset>

              <button className="btn btn-warning mt-3" type="submit" disabled={!selectedEmail || submitting}>
                {submitting ? 'Updating...' : 'Update Information'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateUserInformationPage;